import { canonicalizeModelId } from '../../constants/ai';

export const SUMMARY_LENGTH_OPTIONS = [
  { value: 'short', label: 'Short', description: 'Concise (~256 tokens)' },
  { value: 'medium', label: 'Medium', description: 'Expanded (~512 tokens)' },
  { value: 'long', label: 'Long', description: 'In depth (~1,024 tokens)' },
  { value: 'xl', label: 'XL', description: 'Detailed (~2,048 tokens)' },
  { value: 'xxl', label: 'XXL', description: 'Max context (~4,096 tokens)' },
] as const;

const SUMMARY_LENGTH_OPTION_BY_VALUE = new Map<string, (typeof SUMMARY_LENGTH_OPTIONS)[number]>(
  SUMMARY_LENGTH_OPTIONS.map((option) => [option.value, option])
);

export const DEFAULT_SUMMARY_LENGTH_INDEX = 1;

export type SummaryLengthOption = (typeof SUMMARY_LENGTH_OPTIONS)[number]['value'];

export type ModelOption = 'model.reasoning.fast' | 'model.reasoning.balanced' | 'model.reasoning.premium';

export const MODEL_OPTIONS: readonly ModelOption[] = [
  'model.reasoning.fast',
  'model.reasoning.balanced',
  'model.reasoning.premium',
] as const;

export const MODEL_LABEL: Record<ModelOption, string> = {
  'model.reasoning.fast': 'Fast',
  'model.reasoning.balanced': 'Balanced',
  'model.reasoning.premium': 'Premium',
} as const;

const MODEL_OPTION_SET: ReadonlySet<string> = new Set(MODEL_OPTIONS);
const MODEL_OPTION_BY_CANONICAL = new Map<string, ModelOption>();
for (const option of MODEL_OPTIONS) {
  const canonical = canonicalizeModelId(option);
  if (canonical) {
    MODEL_OPTION_BY_CANONICAL.set(canonical, option);
  }
}

export function getSummaryLengthOptionByIndex(index: number): (typeof SUMMARY_LENGTH_OPTIONS)[number] | null {
  if (!Number.isInteger(index)) {
    return null;
  }
  if (index < 0 || index >= SUMMARY_LENGTH_OPTIONS.length) {
    return null;
  }
  return SUMMARY_LENGTH_OPTIONS.at(index) ?? null;
}

export function getModelLabel(model: ModelOption): string {
  return MODEL_LABEL[model];
}

export function isModelOption(value: unknown): value is ModelOption {
  return typeof value === 'string' && MODEL_OPTION_SET.has(value);
}

export function canonicalizeSummaryModel(model: ModelOption): ModelOption {
  const canonical = canonicalizeModelId(model);
  return isModelOption(canonical) ? canonical : model;
}

export function resolveModelOptionFromCanonical(canonical: string): ModelOption | null {
  return MODEL_OPTION_BY_CANONICAL.get(canonical) ?? null;
}

export function getSummaryLengthLabel(value: SummaryLengthOption | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const match = SUMMARY_LENGTH_OPTION_BY_VALUE.get(value);
  return match?.label ?? null;
}

export function isSummaryLengthOption(value: unknown): value is SummaryLengthOption {
  return typeof value === 'string' && SUMMARY_LENGTH_OPTION_BY_VALUE.has(value);
}
